import QuoteHelp from './QuoteHelp';
import React from 'react';
import { quoteDocumentReadiness } from '../quoteDocumentReadiness';
import {
  availableQuoteDocumentTypes,
  canViewQuoteDocument,
  formatDocumentSize,
  quoteDocumentGedPath,
  quoteDocumentIsArchived,
  quoteDocumentIsOfficial,
  quoteDocumentLabel,
  sortQuoteDocuments,
} from '../quoteDocumentModel';
import {
  QUOTE_VERSION_STATUS,
  isQuoteVersionOfficial,
} from '../quoteVersionStatus';

const dateTime = (value) => value
  ? new Intl.DateTimeFormat('fr-FR', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value))
  : '-';

const QuoteDocumentsPanel = ({
  editor,
  calculated,
  calculationStatus,
  saving,
  onPreview,
  onPreviewPrintTemplate,
  onPreviewInternalTemplate,
  onPrintInternalTemplate,
  onGenerateOfficial,
  onArchiveGed,
  onDownload,
}) => {
  const documentTypes = availableQuoteDocumentTypes(editor.permissions);
  const documents = sortQuoteDocuments(editor.documents || []).filter(
    (document) => canViewQuoteDocument(document, editor.permissions)
  );
  const currencySupported = (editor.currencyCode ?? 'EUR') === 'EUR';
  const documentReadiness = quoteDocumentReadiness({ editor, calculated, calculationStatus });
  const canPreview = currencySupported && [QUOTE_VERSION_STATUS.DRAFT, QUOTE_VERSION_STATUS.VALIDATED].includes(editor.versionStatus);
  const canGenerateOfficial = currencySupported && [QUOTE_VERSION_STATUS.ISSUED, QUOTE_VERSION_STATUS.ACCEPTED].includes(editor.versionStatus)
    && Boolean(editor.permissions?.canGenerateOfficial ?? editor.permissions?.canIssue);

  return (
    <section className="quote-documents-panel quote-panel">
      <header className="quote-documents-panel__header">
        <div>
          <h2>Documents de la version <QuoteHelp title="Aperçus et impressions">Chaque aperçu utilise le même gabarit que son impression : devis client, minute de chiffrage ou méthodes de calcul.</QuoteHelp></h2>
        </div>
        <div className="quote-document-workflow">
          <span className={canPreview ? 'is-current' : ''}>1. Prévisualiser</span>
          <span className={editor.versionStatus === QUOTE_VERSION_STATUS.VALIDATED ? 'is-current' : ''}>2. Valider</span>
          <span className={isQuoteVersionOfficial(editor.versionStatus) ? 'is-current' : ''}>3. Émettre</span>
          <span className={documents.some(quoteDocumentIsArchived) ? 'is-current' : ''}>4. Archiver GED</span>
        </div>
      </header>

      <div className="quote-document-actions-grid">
        {documentTypes.map((type) => {
          const readiness = quoteDocumentReadiness({ editor, calculated, calculationStatus, documentType: type });
          return (
          <article key={type} className="quote-document-action-card">
            <div>
              <strong>{quoteDocumentLabel(type)}</strong>
              <span>{type === 'client_quote' ? 'Document destiné au client' : 'Document interne'}</span>
            </div>
            <div className="quote-document-action-card__buttons">
              {canPreview && (
                <button
                  className="basic-button"
                  type="button"
                  disabled={saving || !readiness.ready}
                  onClick={() => {
                    if (type === 'client_quote' && onPreviewPrintTemplate) return onPreviewPrintTemplate();
                    if (['minute', 'calculation_detail'].includes(type) && onPreviewInternalTemplate) return onPreviewInternalTemplate(type);
                    return onPreview(type);
                  }}
                >
                  Prévisualiser
                </button>
              )}
              {canPreview && ['minute', 'calculation_detail'].includes(type) && onPrintInternalTemplate && (
                <button className="basic-button" type="button" disabled={saving || !readiness.ready} onClick={() => onPrintInternalTemplate(type)}>
                  Imprimer / PDF
                </button>
              )}
              {canGenerateOfficial && type === 'client_quote' && (
                <button className="basic-button active" type="button" disabled={saving || !documentReadiness.ready} onClick={() => onGenerateOfficial(type)}>
                  Générer un nouveau PDF officiel
                </button>
              )}
            </div>
          </article>
        ); })}
      </div>
      {!documentReadiness.ready && <p role="status">{documentReadiness.message}</p>}

      <div className="quote-documents-list">
        {documents.map((document) => {
          const official = isQuoteVersionOfficial(editor.versionStatus) && quoteDocumentIsOfficial(document);
          const archived = quoteDocumentIsArchived(document);
          const gedPath = quoteDocumentGedPath(document);
          return (
            <article key={document.id} className={official ? 'quote-document-card--official' : ''}>
              <div className="quote-document-card__identity">
                <strong>{quoteDocumentLabel(document.documentType)}</strong>
                <span>{document.fileName || 'Document sans nom'}</span>
              </div>
              <div className="quote-document-card__badges">
                <span className={`quote-document-status quote-document-status--${official ? 'official' : 'preview'}`}>
                  {official ? 'Officiel' : 'Prévisualisation'}
                </span>
                {archived && <span className="quote-document-status quote-document-status--ged">GED</span>}
              </div>
              <dl>
                <div><dt>Format</dt><dd>{document.mimeType || '-'}</dd></div>
                <div><dt>Taille</dt><dd>{formatDocumentSize(document.fileSize)}</dd></div>
                <div><dt>Généré</dt><dd>{dateTime(document.generatedAt || document.createdAt)}</dd></div>
                <div><dt>Auteur</dt><dd>{document.generatedBy ? `#${document.generatedBy}` : '-'}</dd></div>
              </dl>
              {gedPath && <p className="quote-document-ged-path" title={gedPath}>GED : {gedPath}</p>}
              <small title={document.fileHash}>SHA-256 : {document.fileHash || '-'}</small>
              <div className="quote-document-card__actions">
                <button className="basic-button" type="button" disabled={saving} onClick={() => onDownload(document)}>
                  Télécharger
                </button>
                {official && !archived && editor.permissions?.canArchive && (
                  <button className="basic-button active" type="button" disabled={saving} onClick={() => onArchiveGed(document)}>
                    Archiver dans la GED
                  </button>
                )}
              </div>
            </article>
          );
        })}
        {!documents.length && (
          <div className="quote-document-empty">
            <strong>Aucun document généré</strong>
            <p>Commencez par prévisualiser le devis client, puis validez et émettez la version pour créer ses documents officiels.</p>
          </div>
        )}
      </div>

      {isQuoteVersionOfficial(editor.versionStatus) && (
        <p className="quote-official-document-notice">
          Cette version est figée. Toute modification du contenu nécessite la création d’une nouvelle version.
        </p>
      )}
    </section>
  );
};

export default QuoteDocumentsPanel;
